// proAgentClient.ts — frontend consumer for /api/agent (Pro Mode).
//
// Talks to the Vercel Edge function that runs Groq Llama 3.3 70B via the
// Vercel AI SDK. Parses the AI SDK's UI message stream (SSE, JSON-per-line)
// and emits typed callbacks the existing /ai trace UI can consume.

import type { Chunk } from "@/data/corpus";

export type ProAgentCallbacks = {
  onTextDelta: (delta: string) => void;
  onToolStart: (name: string, callId: string) => void;
  onToolInputDelta?: (callId: string, delta: string) => void;
  onToolInputEnd?: (callId: string, fullJson: string) => void;
  onToolOutput: (callId: string, output: unknown) => void;
  onFinish: (finalText: string) => void;
  onError: (kind: "rate_limit" | "unconfigured" | "agent_error" | "network", message: string) => void;
};

export type ProAgentRequest = {
  query: string;
  history: Array<{ role: "user" | "assistant"; content: string }>;
  corpusContext: Array<Pick<Chunk, "id" | "text">>;
  signal?: AbortSignal;
};

// Errors returned by /api/agent before the stream opens.
type ErrorJson = { error?: string; message?: string };

export async function runProAgent(req: ProAgentRequest, cb: ProAgentCallbacks): Promise<void> {
  let res: Response;
  try {
    res = await fetch("/api/agent", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        query: req.query,
        history: req.history,
        corpusContext: req.corpusContext,
      }),
      signal: req.signal,
    });
  } catch (e) {
    cb.onError("network", e instanceof Error ? e.message : "network error");
    return;
  }

  if (!res.ok || !res.body) {
    // Try to parse error JSON; fall back to status text.
    let body: ErrorJson | null = null;
    try {
      body = (await res.json()) as ErrorJson;
    } catch {
      /* not JSON */
    }
    const kind =
      res.status === 429
        ? "rate_limit"
        : res.status === 503 || body?.error === "unconfigured"
          ? "unconfigured"
          : "agent_error";
    cb.onError(kind, body?.message ?? `HTTP ${res.status}`);
    return;
  }

  // Stream parser — UI message stream is SSE: `data: {json}\n\n`.
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalText = "";

  while (true) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (e) {
      cb.onError("network", e instanceof Error ? e.message : "stream read failed");
      return;
    }
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });

    // SSE messages end with a blank line; events are split by \n\n.
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      sep = buffer.indexOf("\n\n");

      // Each frame is one or more `data: ...` lines; concatenate payloads.
      const dataLines = frame
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trimStart());
      if (dataLines.length === 0) continue;
      const payload = dataLines.join("\n");
      if (payload === "[DONE]") continue; // some SDK versions emit this sentinel

      let evt: { type?: string; [k: string]: unknown };
      try {
        evt = JSON.parse(payload);
      } catch {
        continue; // skip malformed frames
      }

      switch (evt.type) {
        case "text-delta": {
          const delta = typeof evt.delta === "string" ? evt.delta : "";
          if (delta) {
            finalText += delta;
            cb.onTextDelta(delta);
          }
          break;
        }
        case "tool-input-start": {
          const name = typeof evt.toolName === "string" ? evt.toolName : "unknown";
          const id = typeof evt.toolCallId === "string" ? evt.toolCallId : "";
          cb.onToolStart(name, id);
          break;
        }
        case "tool-input-delta": {
          if (!cb.onToolInputDelta) break;
          const id = typeof evt.toolCallId === "string" ? evt.toolCallId : "";
          const d = typeof evt.inputTextDelta === "string" ? evt.inputTextDelta : "";
          if (id && d) cb.onToolInputDelta(id, d);
          break;
        }
        case "tool-input-available":
        case "tool-input-end": {
          if (!cb.onToolInputEnd) break;
          const id = typeof evt.toolCallId === "string" ? evt.toolCallId : "";
          const input = (evt as { input?: unknown }).input;
          const json = input !== undefined ? JSON.stringify(input) : "";
          if (id) cb.onToolInputEnd(id, json);
          break;
        }
        case "tool-output-available": {
          const id = typeof evt.toolCallId === "string" ? evt.toolCallId : "";
          const output = (evt as { output?: unknown }).output;
          if (id) cb.onToolOutput(id, output);
          break;
        }
        case "error": {
          const m =
            typeof (evt as { errorText?: unknown }).errorText === "string"
              ? (evt as { errorText: string }).errorText
              : typeof (evt as { message?: unknown }).message === "string"
                ? (evt as { message: string }).message
                : "agent stream error";
          cb.onError("agent_error", m);
          return;
        }
        default:
          // Many event types (start, text-start, text-end, start-step, finish-step,
          // finish, message-metadata) we don't need — silently skip.
          break;
      }
    }
  }
  cb.onFinish(finalText);
}
